import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { StatusSelect } from "../shared/StatusSelect";
import { LuTrash2, LuFileText } from "react-icons/lu";

// Visible / Hidden reuses the same control as Bills and Monthly Services rather
// than being a third way of showing a two-state value.
const VISIBILITY_OPTIONS = ["visible", "hidden"];
const VISIBILITY_CFG = {
  visible: { label: "Visible", bg: "#dcfce7", color: "#16a34a" },
  hidden: { label: "Hidden", bg: "#f1f5f9", color: "#64748b" },
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
};

// Training material the office shares with named staff.
//
// A document reaches someone only when it is switched on AND they are ticked.
// Both gates are enforced server-side on the queryset; this page is where they
// get set.
export function TrainingDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/training-documents/`),
      axios.get(`${API}/technicians/`),
    ])
      .then(([docRes, peopleRes]) => {
        setDocuments(docRes.data || []);
        setPeople(peopleRes.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const nameOf = (p) =>
    [p.first_name, p.last_name].filter(Boolean).join(" ") || p.username;

  const resetForm = () => {
    setFile(null);
    setTitle("");
    setDescription("");
    setPicked([]);
    setError("");
  };

  const upload = async () => {
    if (!file) {
      setError("Choose a PDF first.");
      return;
    }
    if (!title.trim()) {
      setError("Give it a title.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      if (description.trim()) fd.append("description", description.trim());
      // Repeated keys rather than a JSON array — this is a multipart body.
      picked.forEach((id) => fd.append("allowed_users", id));
      await axios.post(`${API}/training-documents/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      resetForm();
      setShowAdd(false);
      load();
    } catch (e) {
      setError(
        e.response?.data?.file?.[0] || "That did not upload. It needs to be a PDF.",
      );
    }
    setBusy(false);
  };

  const setVisibility = (doc, next) => {
    axios
      .patch(`${API}/training-documents/${doc.id}/`, {
        is_visible: next === "visible",
      })
      .then(load)
      .catch(() => {});
  };

  const toggleViewer = (doc, userId) => {
    const current = doc.allowed_users || [];
    const next = current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId];
    axios
      .patch(`${API}/training-documents/${doc.id}/`, { allowed_users: next })
      .then(load)
      .catch(() => {});
  };

  const remove = (id) => {
    axios
      .delete(`${API}/training-documents/${id}/`)
      .then(() => {
        setConfirmDelete(null);
        load();
      })
      .catch(() => setConfirmDelete(null));
  };

  const togglePick = (id) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Training Documents</div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.searchBar}>
            <div style={{ fontSize: "13px", color: colors.slate, flex: 1 }}>
              PDFs shared with named staff. A document reaches someone only when
              it is Visible and they are ticked.
            </div>
            <button
              style={styles.addButton}
              onClick={() => {
                resetForm();
                setShowAdd((v) => !v);
              }}
            >
              {showAdd ? "Cancel" : "+ Add Document"}
            </button>
          </div>

          {showAdd && (
            <div
              style={{
                padding: "16px",
                borderBottom: `1px solid ${colors.border}`,
                backgroundColor: "#fafafa",
              }}
            >
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ marginBottom: "12px", display: "block" }}
              />
              <input
                style={{ ...inputStyle, marginBottom: "10px" }}
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <textarea
                style={{
                  ...inputStyle,
                  height: "60px",
                  resize: "vertical",
                  marginBottom: "12px",
                }}
                placeholder="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#666",
                  textTransform: "uppercase",
                  marginBottom: "8px",
                }}
              >
                Who can see it
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                {people.map((p) => (
                  <label
                    key={p.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "13px",
                      color: colors.text,
                      cursor: "pointer",
                      padding: "6px 10px",
                      border: `1px solid ${colors.border}`,
                      borderRadius: "6px",
                      backgroundColor: colors.white,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={picked.includes(p.id)}
                      onChange={() => togglePick(p.id)}
                    />
                    {nameOf(p)}
                  </label>
                ))}
              </div>
              {error && (
                <div
                  style={{
                    color: colors.red,
                    fontSize: "13px",
                    marginBottom: "10px",
                  }}
                >
                  {error}
                </div>
              )}
              <button style={styles.addButton} onClick={upload} disabled={busy}>
                {busy ? "Uploading..." : "Upload"}
              </button>
            </div>
          )}

          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : documents.length === 0 ? (
            <div style={styles.loading}>
              No training documents yet. Click + Add Document to share one.
            </div>
          ) : (
            <>
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  style={{
                    padding: "14px 16px",
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <LuFileText
                      size={20}
                      style={{ color: colors.slate, flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: colors.text,
                        }}
                      >
                        {doc.title}
                      </div>
                      {doc.description && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: colors.slate,
                            marginTop: "2px",
                          }}
                        >
                          {doc.description}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#aaa",
                          marginTop: "3px",
                        }}
                      >
                        {formatSize(doc.file_size)}
                        {doc.uploaded_by_name ? ` · ${doc.uploaded_by_name}` : ""}
                      </div>
                    </div>
                    <StatusSelect
                      value={doc.is_visible ? "visible" : "hidden"}
                      options={VISIBILITY_OPTIONS}
                      cfg={VISIBILITY_CFG}
                      onChange={(next) => setVisibility(doc, next)}
                      title="Show or withdraw this document"
                    />
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: `1px solid ${colors.border}`,
                        color: colors.slate,
                        fontSize: "12px",
                        fontWeight: "600",
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Open
                    </a>
                    <button
                      onClick={() =>
                        confirmDelete === doc.id
                          ? remove(doc.id)
                          : setConfirmDelete(doc.id)
                      }
                      onBlur={() =>
                        confirmDelete === doc.id && setConfirmDelete(null)
                      }
                      title={
                        confirmDelete === doc.id
                          ? "Click again to confirm"
                          : "Delete"
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: colors.slate,
                        fontSize: confirmDelete === doc.id ? "11px" : "inherit",
                        fontWeight: "600",
                        padding: "4px",
                      }}
                    >
                      {confirmDelete === doc.id ? (
                        "Delete?"
                      ) : (
                        <LuTrash2 size={15} />
                      )}
                    </button>
                  </div>

                  {/* Who it reaches. A document switched on but shared with
                      nobody is the likeliest mistake, so say so plainly. */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      marginTop: "10px",
                      paddingLeft: "32px",
                    }}
                  >
                    {people.map((p) => {
                      const on = (doc.allowed_users || []).includes(p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => toggleViewer(doc, p.id)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: "20px",
                            border: `1px solid ${on ? colors.slate : colors.border}`,
                            backgroundColor: on ? colors.slate : colors.white,
                            color: on ? colors.white : "#64748b",
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          {nameOf(p)}
                        </button>
                      );
                    })}
                    {(doc.allowed_users || []).length === 0 && (
                      <span
                        style={{
                          fontSize: "11px",
                          color: colors.amber,
                          alignSelf: "center",
                        }}
                      >
                        Shared with nobody — pick who can see it
                      </span>
                    )}
                  </div>
                </div>
              ))}
              <div style={styles.count}>
                {documents.length} Document{documents.length !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
